<template>
  <div class="container-fluid mb-6">
    <div class="row">
      <div class="col-12">
        <date-based-page-header
          v-model="curDate"
          calendar-unit="daily"
        >
          <template slot="desktop-control-buttons">
            <el-button
              v-if="!isLightGreyPlan && isCreateWodButtonVisible"
              :class="wodStyle"
              class="calendar icon-btn"
              @click="handleClickWod"
            />
            <course-filter-select
              v-model="filteringCourses"
              :courses="childCourses"
              :is-loading="isDashboardCourseLoading && isDashboardMemberLoading"
              @change="onChangeFilterValues"
              @click="handleFilterButtonClick"
            />
          </template>
          <template slot="mobile-control-buttons">
            <el-button
              v-if="!isLightGreyPlan && isCreateWodButtonVisible"
              :class="wodStyle"
              class="calendar icon-btn"
              @click="handleClickWod"
            />
            <course-filter-select
              v-model="filteringCourses"
              :courses="childCourses"
              :is-loading="isDashboardCourseLoading && isDashboardMemberLoading"
              @change="onChangeFilterValues"
              @click="handleFilterButtonClick"
            />
          </template>
        </date-based-page-header>
      </div>
    </div>
    <div class="row">
      <div
        v-show="isBookingSystem"
        class="col-12 col-lg-6 col-xl"
      >
        <card
          v-loading="isDashboardCourseLoading"
          :title="`<strong>${cardPreTitle}</strong> 예약 인원`"
          :count="dashboard.courses.counts.booking"
        >
          <img src="/image/icon/dashboard/rsv.svg">
        </card>
      </div>
      <div class="col-12 col-lg-6 col-xl">
        <card
          v-loading="isDashboardCourseLoading"
          :title="`<strong>${cardPreTitle}</strong> 출석 인원`"
          :count="dashboard.courses.counts.attendance"
        >
          <img src="/image/icon/dashboard/attend.svg">
        </card>
      </div>
      <div class="col-12 col-lg-6 col-xl">
        <card
          v-loading="isDashboardCourseLoading"
          :title="`<strong>${cardPreTitle}</strong> 지각 인원`"
          :count="dashboard.courses.counts.late"
        >
          <img
            src="/image/icon/dashboard/late.svg"
            alt=""
          >
        </card>
      </div>
      <div class="col-12 col-lg-6 col-xl">
        <card
          v-loading="isDashboardCourseLoading"
          :title="`<strong>${cardPreTitle}</strong> 결석 인원`"
          :count="dashboard.courses.counts.absence"
        >
          <img
            src="/image/icon/dashboard/absence.svg"
            alt=""
          >
        </card>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <lesson-status-table
          :loading="isDashboardCourseLoading"
          :data="dashboard.courses.results"
          :is-filtered="isFiltered"
          @update="syncDashboard"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-lg-8">
        <lesson-participate-table
          :loading="isDashboardMemberLoading"
          :data="dashboard.members.results"
          :total="dashboard.members.total"
          :is-filtered="isFiltered"
          :is-all-data-fetch="isAllMemberDataFetchComplete"
          :selected-status="statusFilters" 
          @update="syncDashboard"
          @filter-change="handleChangeStatusFilters"
          @scroll="handleScroll"
        />
      </div>
      <div class="col-lg-4">
        <div class="card">
          <div class="card-header">
            <h4 class="card-header-title">
              <strong>{{ cardPreTitle }}</strong> 연령대 분석
            </h4>
          </div>
          <div class="chart card__circle-graph">
            <canvas
              v-circle-chart="circleChartData"
              class="chart-canvas"
            />
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h4 class="card-header-title">
              <strong>{{ cardPreTitle }}</strong> 성별 분석
            </h4>
          </div>
          <div>
            <gender-distribution-graph :data="dashboard.members.counts" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import dateFns from 'date-fns';
import circleChart from '@/directives/CircleChart.js';
import Card from './Card';
import LessonStatusTable from './LessonStatusTable';
import Picker from 'element-ui/packages/date-picker/src/picker';
import LessonParticipateTable from './LessonParticipateTable';
import GenderDistributionGraph from './GenderDistributionGraph';
import FilterDialog from './FilterDialog';
import CourseFilterSelect from './CourseFilterSelect';
import FiflFooter from '@/components/FiflFooter';
import DateBasedPageHeader from '@/components/DateBasedPageHeader';
import { computedGetters } from '@/utils/vuex-helper';
import { fiflboxApi } from '@/utils/api';

export default {
  name: 'Dashboard',
  components: {
    DateBasedPageHeader,
    FiflFooter,
    CourseFilterSelect,
    FilterDialog,
    GenderDistributionGraph,
    LessonParticipateTable,
    Picker,
    LessonStatusTable,
    Card
  },
  directives: {
    circleChart
  },
  data() {
    return {
      curDate: new Date(),
      childCourses: [],
      wods: [],
      filteringCourses: [],
      courseFilterValues: [],
      curDateCourses: [],
      isDashboardCourseLoading: false,
      isDashboardMemberLoading: false,
      isAllMemberDataFetchComplete: false,
      statusFilters: [],
      dashboard: {
        courses: {
          counts: {
            booking: 0,
            attendance: 0,
            late: 0,
            absence: 0
          },
          results: []
        },
        members: {
          counts: {
            ages: [],
            female: 0,
            male: 0
          },
          results: [],
          total: 0,
          curr_offset: 0,
          next_offset: 0
        }
      }
    };
  },
  computed: {
    ...computedGetters(['isCreateWodButtonVisible', 'isDashboardMenuVisible']),
    wodStyle() {
      if (this.wods.length === 0) {
        return 'ic-wod-inactive';
      }

      return 'ic-wod';
    },
    isFiltered() {
      return this.courseFilterValues.length > 0;
    },
    cardPreTitle() {
      return this.isFiltered ? '필터된' : '오늘 전체';
    },
    isBookingSystem() {
      return this.$store.getters.getIsBookingSystem;
    },
    isLightGreyPlan() {
      return this.$store.getters.getIsLightGreyPlan;
    },
    circleChartData() {
      return {
        labels: ['10대 이하', '20대', '30대', '40대', '50대 이상'],
        data: this.dashboard.members.counts.ages,
        options: {
          isLegendVisible: true
        }
      };
    }
  },
  watch: {
    async curDate(value) {
      this.onChangeDateHandler(value);
    },
    isDashboardMenuVisible: {
      immediate: true,
      handler: function(isAllowVisible) {
        if (!isAllowVisible) {
          this.$router.push('/announce/access-block').catch(() => {});
        }
      }
    }
  },
  async created() {
    this.filteringCourses = this.$localStore.getValue('filteringCourses') || [];
    this.curDate = new Date();
  },
  methods: {
    handleClickToday() {
      this.curDate = new Date();
    },
    async syncWods() {
      let { data: wods } = await fiflboxApi.v1.getWods({
        date_after: dateFns.format(this.curDate, 'YYYY-MM-DD'),
        date_before: dateFns.format(this.curDate, 'YYYY-MM-DD')
      });

      this.wods = wods;
    },
    async syncChildCourses(date) {
      let dateString = dateFns.format(date, 'YYYY-MM-DD');
      let { data: childCourses } = await fiflboxApi.v2.getChildCourses({
        date_after: dateString,
        date_before: dateString
      });
      if (childCourses && childCourses[dateString]) {
        this.childCourses = childCourses[dateString].sort((courseA, courseB) =>
          dateFns.compareAsc(courseA.dt, courseB.dt)
        );
      } else {
        this.childCourses = [];
      }
    },
    async onChangeFilterValues(values) {
      this.onChangeFilterHandler(values);
    },
    async syncDashboard() {
      await this.dashboardDataInitialized();
      this.syncDashboardCourses(this.courseFilterValues);
      this.syncDashboardMembers(this.courseFilterValues);
    },
    async syncDashboardCourses(courseFilterValues) {
      this.isDashboardCourseLoading = true;
      let childCourseIds = [];

      if (courseFilterValues) {
        childCourseIds = courseFilterValues.map(course => course.child_course);
      }

      let { data: dashboardCourses } = await fiflboxApi.v1.getDashBoardCourses({
        dt_after: dateFns.startOfDay(this.curDate),
        dt_before: dateFns.endOfDay(this.curDate),
        child_course: childCourseIds
      });
      dashboardCourses.results = dashboardCourses.results.sort((a, b) =>
        dateFns.compareAsc(a.start, b.start)
      );

      this.dashboard.courses = dashboardCourses;
      this.isDashboardCourseLoading = false;
    },
    async syncDashboardMembers(courseFilterValues, isInit = true) {
      // 첫 Fetch 일때만 테이블을 로드합니다. 나머지 로딩은 테이블 내에서, 무한스크롤로 제어합니다.
      if (isInit) {
        this.isDashboardMemberLoading = true;
      }

      let childCourseIds = [];

      if (courseFilterValues) {
        childCourseIds = courseFilterValues.map(course => course.child_course);
      }

      let { data: members } = await fiflboxApi.v1.getDashBoardMembers({
        dt_after: dateFns.startOfDay(this.curDate),
        dt_before: dateFns.endOfDay(this.curDate),
        child_course: childCourseIds,
        offset: isInit ? 0 : this.dashboard.members.next_offset,
        status: this.statusFilters
      });

      members.results = members.results.sort((a, b) =>
        dateFns.compareDesc(a.modified, b.modified)
      );

      if (isInit) {
        this.dashboard.members = members;
        if (members.total === members.results.length) {
          this.isAllMemberDataFetchComplete = true;
          this.isDashboardMemberLoading = false;
          return;
        }
      } else {
        const tempMembers = this.dashboard.members.results.slice();
        if (this.dashboard.members.curr_offset === members.curr_offset) {
          this.dashboard.members = {
            ...members,
            results: [...tempMembers]
          };
        } else {
          this.dashboard.members = {
            ...members,
            counts: this.dashboard.members.counts,
            results: [...tempMembers, ...members.results]
          };
        }
      }

      if (members.total === this.dashboard.members.results.length) {
        this.isAllMemberDataFetchComplete = true;
        return;
      }
      if (this.isDashboardMemberLoading) {
        this.isDashboardMemberLoading = false;
      }
      return;
    },
    handleClickWod() {
      if (this.wods.length > 0) {
        return this.$router.push(`/wods/${this.wods[0].id}`).catch(() => {});
      }

      return this.$router
        .push({
          path: '/wods',
          query: {
            date: dateFns.format(this.curDate, 'YYYY-MM-DD')
          }
        })
        .catch(() => {});
    },
    onChangeDateHandler: _.debounce(async function(dt) {
      await this.syncChildCourses(dt);
      await this.onChangeFilterValues(this.filteringCourses);
      await this.syncWods();
    }, 300),
    onChangeFilterHandler: _.debounce(async function(values) {
      let courses = values
        .map(value => {
          return this.childCourses.find(course => course.id === value);
        })
        .filter(value => value);

      this.courseFilterValues = courses.map(course => {
        return {
          dt: course.dt,
          child_course: course.id
        };
      });

      await this.syncDashboard();

      this.$localStore.setValue('filteringCourses', values);
    }, 700),
    handleFilterButtonClick(payload) {
      this.onChangeFilterValues(payload.data);
    },
    async handleScroll() {
      if (this.dashboard.members.total === this.dashboard.members.next_offset) {
        this.isAllMemberDataFetchComplete = true;
        return;
      }
      await this.syncDashboardMembers(this.courseFilterValues, false);
    },
    dashboardDataInitialized() {
      this.isAllMemberDataFetchComplete = false;
      this.dashboard.courses.results = [];
      this.dashboard.members.results = [];
      this.statusFilters = [];
    },
    dashboardDataOnlyMemberInitialized() {
      this.isAllMemberDataFetchComplete = false;
      this.dashboard.members.results = [];
    },
    async handleChangeStatusFilters(payload) {
      await this.dashboardDataOnlyMemberInitialized();
      this.statusFilters = await payload;
      await this.syncDashboardMembers(this.courseFilterValues, true);
    }
  }
};
</script>
<style lang="scss">
.dashboard-header {
  position: relative;
  height: 42px;
  margin-top: 36px;
  margin-bottom: 36px;

  h1 {
    text-align: center;
    line-height: 42px;
    font-size: 25px;
    letter-spacing: -1px;
    color: #4a4a4a;
  }

  .left-calendar-control {
    position: absolute;
    top: 0;
    left: 0;
    .el-button + .el-button {
      margin-left: 2px;
    }
  }

  .right-calendar-control {
    position: absolute;
    top: 0;
    right: 0;
  }

  @media screen and (max-width: 768px) {
    height: 72px;
    margin: 0;

    .left-calendar-control {
      top: 22px;
    }

    .right-calendar-control {
      top: 14px;
    }
  }
}

.ic-wod-inactive {
  position: relative;
  background-position-x: 9px;
}

.ic-wod {
  position: relative;
  background-position-x: 9px;

  &:before {
    content: '';
    width: 9px;
    height: 9px;
    border-radius: 9px;
    position: absolute;
    right: -3px;
    top: -3px;
    box-shadow: 0 2px 4px 0 rgba(180, 180, 180, 0.5);
    background-color: #4a4a4a;
  }
}

.card__circle-graph {
  max-width: 300px;
  min-height: 369px;
  box-sizing: border-box;
  padding: 26px;
  text-align: center;
  margin: 0 auto;
}
</style>
